import { useCallback, useRef, useState } from 'react';
import { buildLetterOrder, wordStartsWithLetter, type OrderType } from '@alphabet-hunter/shared';
import type { GameView, WordFeedback } from '../types';
import { logSinglePlayerEnd, logSinglePlayerStart } from '../lib/gameLogClient';

interface SinglePlayerState {
  letterOrder: string[];
  currentLetterIndex: number;
  foundLetters: string[];
  score: number;
}

// Fully offline single-player mode — reimplements the same rules as the
// party/ Durable Object (via the shared gameRules module) entirely
// client-side, so it works with no network connection at all. `playerName`
// is captured explicitly in `start()` (into a ref) rather than taken as a
// hook argument, so a demo-mode name chosen and used in the same handler
// can't be read stale from a not-yet-committed React state update.
export function useSinglePlayer() {
  const [sp, setSp] = useState<SinglePlayerState | null>(null);
  const [feedback, setFeedback] = useState<WordFeedback | null>(null);
  const gameIdRef = useRef<string | null>(null);
  const nameRef = useRef('');

  const start = useCallback((orderType: OrderType, playerName: string) => {
    nameRef.current = playerName;
    setSp({ letterOrder: buildLetterOrder(orderType), currentLetterIndex: 0, foundLetters: [], score: 0 });
    setFeedback(null);
    gameIdRef.current = crypto.randomUUID();
    void logSinglePlayerStart(gameIdRef.current, orderType, playerName);
  }, []);

  const submitWord = useCallback(
    (word: string) => {
      if (!sp) return;
      const trimmed = word.trim();
      const currentLetter = sp.letterOrder[sp.currentLetterIndex];

      if (!trimmed) {
        setFeedback({ message: 'Please type a word.', type: 'error' });
        return;
      }

      if (!wordStartsWithLetter(trimmed, currentLetter)) {
        setFeedback({
          message: `"${trimmed}" does not start with "${currentLetter}". Keep looking!`,
          type: 'error',
        });
        return;
      }

      const nextScore = sp.score + 1;
      const nextIndex = sp.currentLetterIndex + 1;
      setSp({
        letterOrder: sp.letterOrder,
        currentLetterIndex: nextIndex,
        foundLetters: [...sp.foundLetters, currentLetter],
        score: nextScore,
      });

      if (nextIndex >= 26) {
        setFeedback(null);
        if (gameIdRef.current) void logSinglePlayerEnd(gameIdRef.current, nameRef.current, nextScore);
      } else {
        setFeedback({ message: `"${trimmed}" — nice find! +1 point`, type: 'success' });
      }
    },
    [sp]
  );

  const reset = useCallback(() => {
    setSp(null);
    setFeedback(null);
    gameIdRef.current = null;
  }, []);

  const game: GameView | null = sp
    ? {
        players: [{ name: nameRef.current, score: sp.score }],
        letterOrder: sp.letterOrder,
        currentLetterIndex: sp.currentLetterIndex,
        foundLetters: sp.foundLetters,
        currentLetter: sp.letterOrder[sp.currentLetterIndex] || null,
        nextLetter: sp.letterOrder[sp.currentLetterIndex + 1] || null,
        status: sp.currentLetterIndex >= 26 ? 'finished' : 'playing',
        isSinglePlayer: true,
      }
    : null;

  return { game, feedback, start, submitWord, reset, clearFeedback: () => setFeedback(null) };
}
